// DataTable — table-by-default, card-by-narrow.
// Desktop: sticky header, header-click sort (asc → desc → none), `'ru'` locale
// string sort, pagination bottom-right. Narrow (`<900px`): a vertical list of
// row cards, an optional sort control above the list when any column is
// `sortable`, and a stacked pagination footer below. Mobile and desktop share
// the same `sort` and `page` state — there is one DataTable, two render paths.
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from './Icon.js';
import { useNarrow } from './useNarrow.js';
import { DefaultMobileCard, MobileSortControl, Pagination } from './DataTable.mobile.js';

// WHY: Column<R> is a discriminated union so the compiler can prove which
// access path (`row[key]` vs `render(row)`) is safe per call-site. A
// DataColumn ties `key` to `keyof R & string`, so `row[col.key]` indexes
// without a cast. A RenderColumn keeps an arbitrary string `key` (label
// slot for synthetic columns like 'actions') but mandates `render(row)`.
// Discriminator: `render` is required on RenderColumn, forbidden on
// DataColumn. INVARIANT: a sortable RenderColumn must supply `sortValue`,
// otherwise there is nothing to sort by — enforced at the type level.
export interface DataColumn<R> {
  key: keyof R & string;
  label: string;
  width?: number | string;
  align?: 'left' | 'right' | 'center';
  sortable?: boolean;
  sortValue?: (row: R) => unknown;
  // WHY: an optional header-render slot lets a column draw an icon/custom
  // header; `label` stays required as the header's accessible name and the
  // mobile Select option text — it is never widened to ReactNode, which
  // would ripple into the mobile Select.
  headerRender?: () => ReactNode;
  render?: never;
}

type RenderColumnBase<R> = {
  key: string;
  label: string;
  width?: number | string;
  align?: 'left' | 'right' | 'center';
  render: (row: R) => ReactNode;
  headerRender?: () => ReactNode;
};

export type RenderColumn<R> = RenderColumnBase<R> &
  (
    | { sortable?: false; sortValue?: (row: R) => unknown }
    | { sortable: true; sortValue: (row: R) => unknown }
  );

export type Column<R> = DataColumn<R> | RenderColumn<R>;

export interface Sort {
  key: string;
  dir: 'asc' | 'desc';
}

/**
 * @aiPurpose Arguments handed to a caller-supplied `renderMobileCard`. The
 *   `index` is row-position-on-page (post-sort, post-pagination); `columns`
 *   is the unaltered Column[] so the renderer can introspect labels/keys
 *   without duplicating the spec.
 * @aiInvariant `onSelect` is wired to the table-level `onSelect` prop; the
 *   renderer must invoke it on the user's primary card-activation gesture.
 */
export interface RenderMobileCardArgs<R> {
  row: R;
  index: number;
  columns: Column<R>[];
  onSelect: ((row: R) => void) | undefined;
}

export interface DataTableProps<R> {
  columns: Column<R>[];
  rows: readonly R[];
  getRowKey?: (row: R, index: number) => string | number;
  onSelect?: (row: R) => void;
  pageSize?: number;
  initialSort?: Sort;
  /**
   * Optional controlled sort value, for a page whose rows are sorted by the
   * server. Has no effect unless `onSortChange` is also passed — presence of
   * `onSortChange` is what switches DataTable into controlled mode, not
   * `sort` alone. When controlled, `sort` wins over `initialSort`.
   */
  sort?: Sort | null;
  /**
   * Presence of this prop activates controlled-sort mode: DataTable stops
   * client-sorting `rows` (the caller/server owns row order) and header
   * clicks report the next sort here instead of managing it internally.
   * Controlled header clicks cycle asc → desc only (never emit `null`) since
   * a server-sorted view has no client-owned "no sort" state; the mobile
   * sort control's clear option can still call this with `null`, which the
   * caller must accept. Controlled sort does NOT reset `page` — a
   * paginating caller should reset its own page state on sort change.
   */
  onSortChange?: (sort: Sort | null) => void;
  emptyState?: ReactNode;
  /**
   * Optional custom mobile (`<900px`) card renderer. When omitted, the
   * generic fallback renders columns with a non-empty `label` as labelled
   * fields and columns with an empty `label` as a right-aligned action area.
   * WHY: explicit prop instead of new Column discriminants — keeps the
   * existing Column API untouched while letting feature pages opt into
   * a grouped layout where the generic stacked card is not good enough.
   */
  renderMobileCard?: (args: RenderMobileCardArgs<R>) => ReactNode;
}

function defaultKey<R>(row: R, index: number): string | number {
  if (row && typeof row === 'object' && 'id' in (row as object)) {
    const id = (row as { id?: unknown }).id;
    if (typeof id === 'string' || typeof id === 'number') return id;
  }
  return index;
}

export function DataTable<R>({
  columns,
  rows,
  getRowKey = defaultKey,
  onSelect,
  pageSize = 25,
  initialSort,
  sort: sortProp,
  onSortChange,
  emptyState,
  renderMobileCard,
}: DataTableProps<R>) {
  const { i18n, t } = useTranslation('common');
  const narrow = useNarrow();
  const [internalSort, setInternalSort] = useState<Sort | null>(initialSort ?? null);
  const [page, setPage] = useState(0);

  const controlled = onSortChange !== undefined;
  const activeSort = controlled ? (sortProp ?? null) : internalSort;

  const sorted = useMemo(() => {
    // WHY: controlled mode means the caller (typically a server) owns row
    // order; sorting again here would fight that order on every render.
    if (controlled) return rows;
    if (!activeSort) return rows;
    const col = columns.find((c) => c.key === activeSort.key);
    if (!col) return rows;
    // WHY: DataColumn's `key` is typed `keyof R & string`, so the fallback
    // `r[col.key]` is sound without a cast. RenderColumn has no row-field
    // mapping — sortable RenderColumns must supply `sortValue` (enforced
    // by the type). The runtime guard returns rows unsorted only if a
    // caller bypasses types.
    const get: ((row: R) => unknown) | null = col.sortValue
      ? col.sortValue
      : col.render
        ? null
        : (r: R) => r[col.key];
    if (!get) return rows;
    return [...rows].sort((a, b) => {
      const va = get(a);
      const vb = get(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'number' && typeof vb === 'number')
        return (va - vb) * (activeSort.dir === 'asc' ? 1 : -1);
      return (
        String(va).localeCompare(String(vb), i18n.language) * (activeSort.dir === 'asc' ? 1 : -1)
      );
    });
  }, [rows, activeSort, columns, i18n.language, controlled]);

  const pages = Math.max(1, Math.ceil(sorted.length / pageSize));
  useEffect(() => {
    if (page >= pages) setPage(0);
  }, [pages, page]);
  const pageRows = sorted.slice(page * pageSize, (page + 1) * pageSize);

  function headerClick(col: Column<R>) {
    if (!col.sortable) return;
    if (controlled) {
      // WHY: two-state cycle (asc → desc), never null — a server-sorted
      // view has no "no sort" affordance once a column is active.
      const dir = activeSort?.key === col.key && activeSort.dir === 'asc' ? 'desc' : 'asc';
      onSortChange?.({ key: col.key, dir });
      return;
    }
    setInternalSort((s) => {
      if (!s || s.key !== col.key) return { key: col.key, dir: 'asc' };
      if (s.dir === 'asc') return { key: col.key, dir: 'desc' };
      return null;
    });
  }

  const sortableColumns = useMemo(
    () => columns.filter((c) => c.sortable && c.label.length > 0),
    [columns],
  );

  if (rows.length === 0 && emptyState) return <>{emptyState}</>;

  const pagination =
    pages > 1 ? (
      <Pagination
        page={page}
        pages={pages}
        total={rows.length}
        setPage={setPage}
        compact={narrow}
        labelPageOfTotal={t('pagination.pageOfTotal', {
          page: String(page + 1),
          pages: String(pages),
          total: String(rows.length),
        })}
        labelBack={t('actions.back')}
        labelNext={t('actions.next')}
      />
    ) : null;

  if (narrow) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1, gap: 8 }}>
        {sortableColumns.length > 0 && (
          <MobileSortControl
            columns={sortableColumns}
            sort={activeSort}
            onChange={(next) => (controlled ? onSortChange?.(next) : setInternalSort(next))}
            labelSortBy={t('dataTable.sortBy')}
            labelAsc={t('dataTable.sortDirection.asc')}
            labelDesc={t('dataTable.sortDirection.desc')}
          />
        )}
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            minHeight: 0,
            // WHY: `overflowX: hidden` keeps any over-long card content (long
            // emails, untranslatable tokens) from pushing the page scroll
            // width past `window.innerWidth` at 320px viewports. Vertical
            // auto-scroll is preserved so long lists still scroll.
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {pageRows.map((r, i) => (
            <li key={getRowKey(r, page * pageSize + i)} style={{ listStyle: 'none' }}>
              {renderMobileCard ? (
                renderMobileCard({
                  row: r,
                  index: page * pageSize + i,
                  columns,
                  onSelect,
                })
              ) : (
                <DefaultMobileCard row={r} columns={columns} onSelect={onSelect} />
              )}
            </li>
          ))}
        </ul>
        {pagination}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
      <div
        style={{
          flex: 1,
          overflow: 'auto',
          border: '1px solid var(--border-1)',
          borderRadius: 'var(--r-2)',
          background: 'var(--bg-1)',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead style={{ position: 'sticky', top: 0, background: 'var(--bg-1)', zIndex: 1 }}>
            <tr>
              {columns.map((c) => {
                const ariaSort: 'ascending' | 'descending' | 'none' | undefined = c.sortable
                  ? activeSort?.key === c.key
                    ? activeSort.dir === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : 'none'
                  : undefined;
                return (
                  <th
                    key={c.key}
                    onClick={() => headerClick(c)}
                    // WHY: sortable headers are keyboard-operable; the
                    // focus-visible ring comes from the global
                    // `*:focus-visible` rule in colors_and_type.css — do not
                    // add an inline focus style here.
                    tabIndex={c.sortable ? 0 : undefined}
                    aria-sort={ariaSort}
                    // WHY: when `headerRender` draws a non-text node (e.g. an
                    // icon), aria-label pins the column header's accessible
                    // name to `label`; for a plain string header the visible
                    // text already names it, so aria-label stays undefined
                    // (no redundant override).
                    aria-label={c.headerRender ? c.label : undefined}
                    onKeyDown={
                      c.sortable
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              headerClick(c);
                            }
                          }
                        : undefined
                    }
                    style={{
                      textAlign: c.align || 'left',
                      fontWeight: 500,
                      color: 'var(--fg-2)',
                      padding: '10px 12px',
                      borderBottom: '1px solid var(--border-1)',
                      width: c.width,
                      cursor: c.sortable ? 'pointer' : 'default',
                      userSelect: 'none',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {c.headerRender ? c.headerRender() : c.label}
                      {c.sortable && activeSort?.key === c.key && (
                        <Icon
                          name="chevron-down"
                          size={12}
                          style={{
                            transform: activeSort.dir === 'asc' ? 'rotate(180deg)' : 'none',
                          }}
                        />
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r, i) => (
              <tr
                key={getRowKey(r, i)}
                onClick={() => onSelect && onSelect(r)}
                // WHY: activatable rows are keyboard-operable; the
                // focus-visible ring comes from the global `*:focus-visible`
                // rule in colors_and_type.css — do not add an inline focus
                // style here. The native row role is kept; no role override.
                tabIndex={onSelect ? 0 : -1}
                onKeyDown={
                  onSelect
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onSelect(r);
                        }
                      }
                    : undefined
                }
                style={{
                  cursor: onSelect ? 'pointer' : 'default',
                  background: 'transparent',
                  transition: 'background var(--dur-1) var(--ease-out)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--bg-2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                {columns.map((c) => {
                  const isAction = c.label.length === 0;
                  return (
                    <td
                      key={c.key}
                      // WHY: mirrors the mobile-card action guard — for an
                      // activatable row, an action cell stops click + keydown
                      // from bubbling to the row so an action control without
                      // its own stopPropagation cannot open the row.
                      onClick={isAction && onSelect ? (e) => e.stopPropagation() : undefined}
                      onKeyDown={isAction && onSelect ? (e) => e.stopPropagation() : undefined}
                      style={{
                        padding: '10px 12px',
                        borderBottom: '1px solid var(--border-2)',
                        textAlign: c.align || 'left',
                        verticalAlign: 'middle',
                      }}
                    >
                      {c.render ? c.render(r) : (r[c.key] as ReactNode)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pagination}
    </div>
  );
}
